import React, { FunctionComponent, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { debounce } from 'debounce';
import { connect } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import RegularButton from '../../../../components/RegularButton';
import { saveSurveyRoutine, checkSurveyNameRoutine } from '../../routines';
import { IAppState } from '../../../../common/models/store/IAppState';
import { ICreateSurvey } from '../../../../common/models/survey/ICreateSurvey';
import { removeRequiredQuestion } from '../SurveyBuilder/utils';
import { ISurveyQuestion } from '../../../../common/models/survey/ISurveyQuestion';
import Icon from '../../../../components/Icon';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import styles from './styles.module.scss';

interface IProps {
  name: string;
  description: string;
  isNewNameTaken: boolean;
  isChecking: boolean;
  isSaving: boolean;
  selectedQuestions: ISurveyQuestion[];
  checkSurveyName: IBindingCallback1<{ name: string; isSaveAs: boolean }>;
  saveSurvey: IBindingCallback1<ICreateSurvey>;
  onCancel: IBindingAction;
}

const SaveAsPopup: FunctionComponent<IProps> = ({
  name,
  description,
  isNewNameTaken,
  selectedQuestions,
  isChecking,
  isSaving,
  checkSurveyName,
  saveSurvey,
  onCancel
}) => {
  const [newName, setNewName] = useState(name);
  const [newDescription, setNewDescription] = useState(description);

  const search = debounce((v: string) => checkSurveyName({ name: v.trim(), isSaveAs: true }), 500);

  const onSave = () => {
    saveSurvey({
      name: newName.trim(),
      description: newDescription.trim() || undefined,
      surveyQuestions: removeRequiredQuestion(selectedQuestions)
    });
  };

  useEffect(() => {
    search(newName);
    return () => search.clear();
  }, [newName]);

  return (
    <div className={styles.saveAsContent}>
      <LoaderWrapper transparent loading={isSaving}>
        <header className="sb-header">
          <FormattedMessage id="surveyBuilder.saveAs" defaultMessage="Save As New Survey" />
        </header>
        <main className={styles.saveAsPopupBody}>
          <div>
            <div className={`${isNewNameTaken ? 'survey-name-error' : styles.labelText} ${styles.label}`}>
              {isNewNameTaken
                ? (
                  <>
                    <Icon className={styles.warningIcon} icon="warning" />
                    <FormattedMessage id="surveyBuilder.titleTaken" defaultMessage="Title already in use" />
                  </>
                )
                : <FormattedMessage id="surveyTable.surveyTitle" defaultMessage="Survey Title" />}
            </div>
            <div className="position-relative">
              {isChecking && (
                <Spinner className={styles.spinner} animation="border" size="sm" />
              )}
              <input
                className={`${styles.saveAsPopupInput} 
                  ${styles.saveAsTitleInput} 
                  ${isNewNameTaken ? styles.inputError : ''}`}
                value={newName}
                onChange={({ target }) => setNewName(target.value)}
              />
            </div>
          </div>
          <div>
            <div className={`${styles.labelText} ${styles.label}`}>
              <FormattedMessage id="surveyBuilder.surveyDescription" defaultMessage="Survey Description" />
            </div>
            <input
              className={styles.saveAsPopupInput}
              value={newDescription}
              onChange={({ target }) => setNewDescription(target.value)}
            />
          </div>
        </main>
        <footer className="sb-footer">
          <div className="sb-footer-bttns">
            <RegularButton variant="tertiary" onClick={onCancel} textIntlId="cancel" defaultMessage="Cancel" />
            <RegularButton
              disabled={isNewNameTaken || !newName.trim() || isChecking}
              onClick={onSave}
              textIntlId="continue"
              defaultMessage="Continue"
            />
          </div>
        </footer>
      </LoaderWrapper>
    </div>
  );
};

const mapStateToProps = ({
  surveyBuilder: { description, name, isSaving, isChecking, isNewNameTaken, selectedQuestions }
}: IAppState) => ({ description: description || '', name, isSaving, isChecking, isNewNameTaken, selectedQuestions });

const mapDispatchToProps = {
  saveSurvey: saveSurveyRoutine,
  checkSurveyName: checkSurveyNameRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveAsPopup);
